import { Button, Input, SimpleFormField, Textarea } from '@invent/wl-ui-kit-next/ui';
import { CheckCircle2 } from 'lucide-react';
import * as React from 'react';

import { DemoSelect, MockPanel } from '../chrome/marks';
import { day } from '../format';
import { useDemo, useMockGrant } from '../state/demo-state';

/**
 * Help's contact block (step 6), a badged panel inside the live Help page after "Reaching
 * Veritant", signed in or out — Help is anonymous, because registration's refusals end there before
 * any account exists (the two-portals design, §10 ruling 3).
 *
 * **It prints no address, because Veritant has none.** The owner ruled that the channel will be an
 * email address, and it does not exist yet; a plausible `support@` in a mock is the one sort of
 * placeholder that eventually gets used for real (the onboarding study's `ContactBlock`, for the
 * same reason). So the address is a dashed blank that says it is to come, and what the block can
 * show is the other half of the channel: what to write, and the reference to quote with it.
 *
 * The form is the demo's — a message goes into this page's memory and is listed below it, and
 * nothing leaves the page, which the confirmation says in those words.
 */

const TOPICS = ['Signing in', 'A survey', 'My numbers', 'Something else'] as const;

export function HelpContactPanel({ signedIn }: { signedIn: boolean }) {
  const { state, dispatch } = useDemo();
  const { acts } = useMockGrant('help-contact');
  const [topic, setTopic] = React.useState<string>(TOPICS[0]);
  const [message, setMessage] = React.useState('');
  const [reference, setReference] = React.useState('');
  const [missing, setMissing] = React.useState(false);
  const [received, setReceived] = React.useState(false);
  const messages = state.act1.helpMessages;

  const send = (event: React.FormEvent) => {
    event.preventDefault();
    if (message.trim() === '') {
      setMissing(true);
      setReceived(false);
      return;
    }
    dispatch({ type: 'help/send', topic, message: message.trim(), reference: reference.trim() });
    setMessage('');
    setReference('');
    setReceived(true);
  };

  return (
    <MockPanel
      id="help-contact"
      title="Write to Veritant"
      source="no-endpoint"
      description="How the contact block will read once Veritant publishes its address."
    >
      <div className="grid gap-6 text-[0.9375rem]">
        <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-[auto_minmax(0,1fr)] sm:items-baseline">
          <dt className="text-muted-foreground">Email</dt>
          <dd>
            <span className="inline-flex rounded border border-dashed border-[var(--veritant-yellow-edge)] px-3 py-1 text-[0.875rem] text-muted-foreground">
              address to come
            </span>
            <span className="mt-1 block text-[0.875rem] text-muted-foreground">
              Veritant has not published one yet. It will be an email address, and it will be here.
            </span>
          </dd>
          <dt className="text-muted-foreground">Quote</dt>
          <dd>
            The reference a screen gave you — a registration that cannot continue shows one, and
            each submitted survey has its own.
          </dd>
        </dl>

        <form noValidate onSubmit={send} className="grid gap-5">
          <fieldset disabled={!acts} className="grid gap-5">
            <SimpleFormField label="Topic" htmlFor="help-topic">
              <DemoSelect
                id="help-topic"
                value={topic}
                onChange={(event) => setTopic(event.target.value)}
                className="w-full"
              >
                {TOPICS.map((entry) => (
                  <option key={entry} value={entry}>
                    {entry}
                  </option>
                ))}
              </DemoSelect>
            </SimpleFormField>
            <SimpleFormField
              label="Message"
              htmlFor="help-message"
              error={missing ? 'Write what you need help with.' : undefined}
              required
            >
              <Textarea
                id="help-message"
                rows={4}
                value={message}
                aria-invalid={missing}
                onChange={(event) => {
                  setMessage(event.target.value);
                  setMissing(false);
                }}
              />
            </SimpleFormField>
            <SimpleFormField
              label="Reference to quote"
              htmlFor="help-reference"
              description={
                'Optional. If a screen gave you a reference, paste it here — it tells Veritant’s ' +
                'research team what happened, and names nobody.'
              }
            >
              <Input
                id="help-reference"
                autoComplete="off"
                spellCheck={false}
                value={reference}
                onChange={(event) => setReference(event.target.value)}
              />
            </SimpleFormField>
          </fieldset>
          <p className="text-[0.875rem] text-muted-foreground">
            {signedIn
              ? 'Veritant will answer at the address you sign in with.'
              : 'Signed out, the real form will also ask where Veritant should answer.'}
          </p>
          <div>
            <Button type="submit" disabled={!acts}>
              Send
            </Button>
          </div>
          <p role="status" className="min-h-6 text-[var(--color-teal)]">
            {received && (
              <span className="inline-flex items-center gap-2">
                <CheckCircle2 className="size-4" aria-hidden />
                Received — in this demo nothing leaves the page.
              </span>
            )}
          </p>
        </form>

        {messages.length > 0 && (
          <section aria-labelledby="help-sent" className="grid gap-2">
            <h3 id="help-sent" className="text-base font-medium text-[var(--color-royal-blue)]">
              Sent this session
            </h3>
            <ul className="grid gap-2">
              {messages.map((sent) => (
                <li key={sent.id} className="grid gap-1 rounded-md border border-border p-3">
                  <span className="flex flex-wrap items-baseline gap-x-3 text-[0.875rem] text-muted-foreground">
                    <span className="font-medium text-[var(--color-midnight-blue)]">{sent.topic}</span>
                    <span>{day(sent.sentAt)}</span>
                    {sent.reference && <span className="font-mono break-all">{sent.reference}</span>}
                  </span>
                  <span className="whitespace-pre-line">{sent.message}</span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </MockPanel>
  );
}
